import { useState } from "react";
import scraperApi from "api";
import { DateRange, DomainDetailsRow, DomainStats, Filters } from "utils/analytics/types";
import { buildAnalyticsApiParams } from "utils/analytics/filters";
import { trackAnalyticsActionClicked } from "utils/Tracking";
import { useLoading } from "hooks/useLoading";
import { useAbortController } from "hooks/useAbortController";

interface UseDomainStatsOptions {
  domain: string;
  dateRange: DateRange;
  filters: Filters;
  itemLocation?: string;
}

/**
 * Type guard that filters out null, undefined, non-strings, and empty strings
 */
const isNonEmptyString = (value: unknown): value is string => {
  return typeof value === 'string' && value.trim() !== '';
};

export const useDomainStats = ({
  domain,
  dateRange,
  filters,
  itemLocation,
}: UseDomainStatsOptions) => {
  const [stats, setStats] = useState<DomainStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const {loading, setLoading} = useLoading(false);
  const { createNewSignal, shouldIgnoreError } = useAbortController();

  const fetchStats = async () => {
    const signal = createNewSignal();

    try {
      setLoading(true);
      setError(null);

      const params = buildAnalyticsApiParams(dateRange, filters);

      // Pass the domain parameter directly and spread all filter params
      // The domain parameter will override any domain from filters
      const res = await scraperApi.analytics.getDomainDetails({
        ...params,
        domain,
      }, { signal });

      if (signal.aborted) return;

      const responseData: DomainDetailsRow[] = res?.data || [];

      // Process and map the response data
      const locationsFromResponse = Array.from(
        new Set(
          responseData
            .map((row) => row.countries)
            .flat()
            .filter(isNonEmptyString)
        )
      );

      // Use locations from detailed API response (groupUniqArray(country) from ClickHouse)
      // If none returned, fall back to itemLocation which comes from table row's any(country) query
      // This handles cases where detail API has no country data (sparse data or old entries)
      const locations =
        locationsFromResponse.length > 0
          ? locationsFromResponse
          : itemLocation
          ? [itemLocation]
          : [];

      // Products are now pre-combined by the backend with product IDs
      // (e.g., ["api", "sde-google", "sde-amazon"])
      const products = Array.from(
        new Set(
          responseData
            .flatMap((row) => row.products || [])
            .filter(isNonEmptyString)
        )
      );

      let responseTimeSum = 0;
      let responseTimeCount = 0;
      let concurrencySum = 0;
      let concurrencyCount = 0;
      for (const row of responseData) {
        responseTimeSum += Number(row.response_time_sum_ms ?? 0);
        responseTimeCount += Number(row.response_time_count ?? 0);
        concurrencySum += Number(row.concurrency_sum ?? 0);
        concurrencyCount += Number(row.concurrency_count ?? 0);
      }

      const mappedDomainStats: DomainStats = {
        avgConcurrency: concurrencyCount > 0 ? concurrencySum / concurrencyCount : 0,
        avgResponseTime: responseTimeCount > 0 ? responseTimeSum / responseTimeCount : 0,
        totalSuccess: responseData.reduce(
          (acc, row) => acc + Number(row.successful_requests),
          0
        ),
        totalFailure: responseData.reduce(
          (acc, row) => acc + Number(row.failed_requests),
          0
        ),
        products,
        countries: locations,
        cumulativeData: responseData.map((row) => ({
          date: row.day,
          cumulative_success: Number(row.successful_requests),
          cumulative_failure: Number(row.failed_requests),
          concurrent_threads: Number(row.avg_concurrency ?? 0),
        })),
      };

      setStats(mappedDomainStats);
      trackAnalyticsActionClicked({
        section: "global_all",
        action: "view_domain_details",
        target: "domain_analytics",
      });
    } catch (err) {
      if (shouldIgnoreError(err)) return;
      setError(err instanceof Error ? err.message : "Failed to load domain details");
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  };

  return {
    stats,
    loading,
    error,
    fetchStats,
  };
};
